// src/pages/Explore.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./css/Explore.css";
// near other imports in Explore.jsx
import {
    fetchAiResults,
    normalizeAiPayload,
    extractOverview,
    saveAiTripToFirestore,
    waitForAuthUser,
    extractTitle, // optional fine-grained helpers
    extractItinerary
} from "../libs/aiSearch";
import { generateStructuredTrip } from "../libs/aiStructuredPrompt";
import TripsCarousel from "../components/TripsCarousel";
// Firestore imports (modular SDK v9+)
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    getDoc,
    doc as fbDoc,
    addDoc,
    serverTimestamp,
    getDocs,
    limit,
    startAfter,
    startAt,
    endAt
} from "firebase/firestore";

import { db } from "../firebase"; // <-- adjust path if needed

// AI trip context (so we can store the selected AI result and navigate to details)
import { useAiTrip } from "../context/AiTripContext";

/**
 * Explore
 * - Fetches trips from Firestore only when opened (if parent hasn't provided trips)
 * - Accepts optional `trips` prop if parent prefers to pass them
 */
export default function Explore({ initialOpen = true, onClose, trips = [], tags = [], initialTag = null } = {}) {
    const [open, setOpen] = useState(Boolean(initialOpen));
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTag, setActiveTag] = useState(null);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [fetchedTrips, setFetchedTrips] = useState(null);
    const [aiResults, setAiResults] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    // Travel Guides pagination
    const [guidePageSize] = useState(8);
    const [guideLastDoc, setGuideLastDoc] = useState(null);
    const [guideHasMore, setGuideHasMore] = useState(true);
    const [loadingMoreGuides, setLoadingMoreGuides] = useState(false);
    const [aiSearchResults, setAiSearchResults] = useState(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const {
        setAiTrip,
        setAiGenerationStatus,
        setPendingAiTrip,
        setAiGenerationError
    } = useAiTrip();

    // --- Trips split by source ---
    const [userTripList, setUserTripList] = useState([]); // trips from "trips" collection
    const [aiTripList, setAiTripList] = useState([]);     // combined AI trips (owned + public)

    // pagination for public AI trips
    const [aiPageSize] = useState(12);
    const [aiPublicLastDoc, setAiPublicLastDoc] = useState(null);
    const [aiPublicHasMore, setAiPublicHasMore] = useState(true);
    const [loadingMoreAiPublic, setLoadingMoreAiPublic] = useState(false);
    const [aiError, setAiError] = useState(null);
    const resultsPaneRef = useRef(null); // used to detect scrolling for infinite load
    const closedDuringGenerationRef = useRef(false);
    const [guideList, setGuideList] = useState([]);
    const loadMoreAiPublicRef = useRef(null);
    const aiPublicLastDocRef = useRef(null);
    const [searchLoading, setSearchLoading] = useState(false);

    // remove or keep the old const for reference, then add:
    const [aiSuggestions, setAiSuggestions] = useState([
        "Paris Trip Itinerary",
        "Weekend Rome Trip",
        "Budget solo trip",
        "Luxury spa weekend",
        "Road trip ideas",
    ]);
    const aiPlaceholderExamples = [
        "5-Day Bali Adventure",
        "4 Days in Paris",
        "3 Days Luxury Dubai",
        "6-Day Switzerland Scenic Tour",
        "Thailand Budget Trip",
        "3N Maldives Luxury Escape",
        "4-Day Goa Party Trip",
        "5-Day Kashmir Snow Trip",
        "7-Day Japan Explorer",
        "3 Days Singapore"
    ];

    const [placeholderIndex, setPlaceholderIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setPlaceholderIndex((prev) =>
                prev === aiPlaceholderExamples.length - 1 ? 0 : prev + 1
            );
        }, 2500);

        return () => clearInterval(interval);
    }, []);

    // apply prop initialTag when Explore opens
    useEffect(() => {
        if (initialTag && open) {
            setActiveTag(initialTag);
            setSearchQuery("");
            // optional: focus input after small delay
            setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
        }
        // if parent later clears initialTag, we don't forcibly clear user's selection
        // so we only set when the overlay opens and initialTag is present
    }, [initialTag, open]);

    useEffect(() => {
        if (initialTag) {
            setActiveTag(initialTag);
        }
    }, []); // or include initialTag if you want updates to reapply

    const makeSlug = (title, dateStr) => {
        if (!title && !dateStr) return null;

        // sanitize title
        const slugFromTitle = (title || "")
            .toLowerCase()
            .replace(/['"‘’“”]/g, "") // remove quotes
            .replace(/[^a-z0-9\s-]/g, " ") // strip non-alnum (keep spaces and dashes)
            .trim()
            .replace(/\s+/g, "-") // spaces -> dashes
            .replace(/-+/g, "-"); // collapse multiple dashes

        // normalize date string to yyyy-mm-dd if possible
        let datePart = "";
        if (dateStr) {
            const d = new Date(dateStr);
            if (!Number.isNaN(d.getTime())) {
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                const dd = String(d.getDate()).padStart(2, "0");
                datePart = `${yyyy}-${mm}-${dd}`;
            } else if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                // already looks like yyyy-mm-dd
                datePart = dateStr.slice(0, 10);
            }
        }

        if (slugFromTitle && datePart) return `${slugFromTitle}-${datePart}`;
        return slugFromTitle || datePart || null;
    };

    // Focus input on open and notify parent when closed
    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
        if (!open && typeof onClose === "function") onClose();
    }, [open, onClose]);

    // Inject lightweight page styles (only once)
    useEffect(() => {
        if (document.getElementById("explore-page-styles")) return;
        const css = `
      .explore-wrap { padding: 28px; min-height: 60vh; display: flex; flex-direction: column; gap: 20px; align-items: stretch; }
      .explore-spot { position: relative; width: 100%; max-width: 1100px; margin: 0 auto; }

      .search-overlay { position: fixed; left: 0; right: 0; top: 0; bottom: 0; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; z-index: 9998; }
      .overlay-backdrop { position: absolute; inset: 0; background: rgba(4,8,15,0.45); backdrop-filter: blur(4px); opacity: 0; transition: opacity 240ms ease; }
      .overlay-backdrop.open { opacity: 1; }

      .search-card { position: relative; width: calc(100% - 48px); max-width: 1100px; background: #ffffff; border-radius: 14px; box-shadow: 0 20px 60px rgba(2,6,23,0.25); padding: 18px; transform: translateY(-8px) scale(0.98); transition: transform 240ms cubic-bezier(.2,.9,.3,1), opacity 200ms ease; opacity: 0; }
      .search-card.open { transform: translateY(0) scale(1); opacity: 1; }

      .search-input-wrap { display:flex; gap:10px; align-items:center; width:100%; }
      .search-input { flex:1; font-size:18px; padding:14px 16px; border-radius: 10px; border: 1px solid #e6eef7; outline: none; box-shadow: none; min-width: 0; }
      .search-input:focus { box-shadow: 0 6px 18px rgba(2,6,23,0.06); border-color: #cfe3ff; }

      .search-actions { margin-left: 8px; display:flex; gap:8px; }
      .btn-ghost { padding:10px 12px; border-radius:10px; border:1px solid #edf2f7; background:#fff; cursor:pointer; }

      .suggestion-row { display:flex; gap:12px; margin-top:12px; align-items:center; flex-wrap:wrap; }
      .tag-pill { padding:6px 10px; border-radius:999px; background:#f1f5f9; font-size:13px; cursor:pointer; border:1px solid #e6eef7; }
      .tag-pill.active { background:#e0f2fe; border-color:#cfe3ff; }

      .results-pane { margin-top: 6px; display:block; max-width:1100px; margin-left:auto; margin-right:auto; }
      .results { margin-top: 14px; display:grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap:12px; }
      .result-card { padding:12px; border-radius:10px; background:#fbfdff; border:1px solid #f1f6fb; cursor:pointer; }
      .result-meta { margin-top:8px; color:#6b7280; font-size:13px; }

      .empty-state { margin-top:14px; color:#6b7280; font-size:14px; }

      @media (max-width: 640px) {
        .search-card { width: calc(100% - 20px); margin: 0 10px; }
        .search-input { font-size:16px; }
      }
    `;
        const s = document.createElement("style");
        s.id = "explore-page-styles";
        s.innerHTML = css;
        document.head.appendChild(s);
    }, []);

    // keyboard handlers
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
            if ((e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                setOpen((v) => !v);
            }
            if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const target = e.target || {};
                const tag = (target.tagName || "").toUpperCase();
                if (!["INPUT", "TEXTAREA"].includes(tag)) {
                    e.preventDefault();
                    setOpen(true);
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const navigateToTrip = async (r) => {
        // Defensive: support many shapes
        const pick = (...keys) => {
            for (const k of keys) {
                if (r && (k in r) && r[k]) return r[k];
            }
            return null;
        };

        // 1) Try any explicit handle fields present on the trip object
        let handle = pick("ownerHandle", "handle", "userHandle", "owner_handle", "ownerHandle", "creatorHandle");

        // 2) If no handle but we have an owner UID, attempt to fetch the user doc to get handle
        // 2) If no handle but we have an owner UID, resolve from "handles" collection
        const possibleOwnerUid =
            r.owner_id ||
            r.ownerId ||
            r.createdByUid ||
            r.createdBy;
        console.log(possibleOwnerUid);
        if (!handle && possibleOwnerUid) {
            try {
                const handlesQuery = query(
                    collection(db, "handles"),
                    where("uid", "==", possibleOwnerUid),
                    limit(1)
                );

                const snap = await getDocs(handlesQuery);

                if (!snap.empty) {
                    const handleDoc = snap.docs[0];
                    handle = handleDoc.id; // 🔥 document ID is the handle ("ride")
                }
            } catch (err) {
                console.warn("Failed to resolve handle from handles collection", err);
            }
        }


        // 3) Build or prefer stored slug
        const slug = (r && r.slug && String(r.slug).trim()) ||
            makeSlug(r?.title || r?.name || r?.tripName, r?.startDate || r?.date || r?.start);

        // 4) Prefer handle+slug (canonical user trip URL)
        if (handle && slug) {
            const cleanedHandle = handle.startsWith("@") ? handle.slice(1) : handle;
            console.debug("Explore navigateToTrip -> handle+slug", { handle: cleanedHandle, slug, id: r?.id || r?.trip_id });
            navigate(`/@${cleanedHandle}/${slug}`);
            setOpen(false);
            return;
        }

        // 5) If handle found but slug missing, navigate to handle root or handle/trips/:id if available
        if (handle && !(slug)) {
            const cleanedHandle = handle.startsWith("@") ? handle.slice(1) : handle;
            if (r?.id || r?.trip_id) {
                navigate(`/@${cleanedHandle}/trips/${r.id || r.trip_id}`);
            } else {
                navigate(`/@${cleanedHandle}`);
            }
            setOpen(false);
            return;
        }

        // 6) If no handle resolved: prefer id-based canonical route (most reliable)
        if (r?.id || r?.trip_id) {
            console.debug("Explore navigateToTrip -> id route fallback", { id: r.id || r.trip_id, slug });
            navigate(`/trips/${r.id || r.trip_id}`);
            setOpen(false);
            return;
        }

        // 7) If only slug available: place at root (avoid using fake handle like @ride)
        if (slug) {
            console.debug("Explore navigateToTrip -> slug-only fallback (root)", { slug });
            navigate(`/${slug}`);
            setOpen(false);
            return;
        }

        // 8) Last resort
        console.debug("Explore navigateToTrip -> nothing usable, go home", { chosen: r });
        navigate("/");
        setOpen(false);
    };

    // --- sensible sample data fallback (uses a minimal shape expected by the UI) ---
    const sampleTags = ["beaches", "backpacking", "mountains", "motorbike", "food", "asia", "solo"];

    // reset fetchedTrips when overlay closes so subscriptions re-init on next open
    useEffect(() => {
        if (!open) {
            setFetchedTrips(null);
        }
    }, [open]);

    // --- Fetch trips lazily when overlay opens (only if parent didn't pass trips) ---
    useEffect(() => {
        if (!open) return;

        // if parent passed trips, skip fetching
        if (Array.isArray(trips) && trips.length > 0) return;

        let unsubPublicTrips = null;
        let unsubAiPublic = null;
        let unsubAiOwned = null;
        let active = true;

        // define onScroll here (so cleanup below can access it)
        const onScroll = () => {
            try {
                const node = resultsPaneRef.current;
                if (!node) return;

                const distance = node.scrollHeight - node.scrollTop - node.clientHeight;

                if (distance < 240) {
                    if (
                        loadMoreAiPublicRef.current &&
                        typeof loadMoreAiPublicRef.current === "function"
                    ) {
                        loadMoreAiPublicRef.current();
                    }
                }
            } catch (e) {
                /* ignore */
            }
        };
        // helper: normalize Firestore doc to a small UI-friendly object
        const normalizeDoc = (d, source = "trips") => {
            const data = d && (typeof d.data === "function" ? d.data() : d.data) || {};
            const storedSlug = (data.slug && String(data.slug).trim()) ||
                (data.slugified && String(data.slugified).trim()) ||
                null;
            return {
                __source: source,
                __key: `${source}::${String(d.id)}`,
                id: d.id,

                title: data.title || data.name || data.tripName || "Untitled",
                excerpt: data.excerpt || data.summary || data.description || "",
                slug: storedSlug,

                tags: Array.isArray(data.tags) ? data.tags : (data.tags ? [data.tags] : []),
                startDate: data.startDate || data.date || data.start || null,
                visibility: data.visibility || "public",

                owner_id: data.owner_id || null,   // 🔥 ADD THIS
                createdByUid: data.createdByUid || data.createdBy || data.owner_id || null,

                createdAt: data.createdAt || null,
                raw: data,
            };

        };

        // local arrays that hold the latest snapshot contents
        let publicArr = [];
        let aiPublicArr = [];
        let aiOwnedArr = [];

        // recompute merged list with clear priority & dedupe
        const recomputeAndSet = () => {
            if (!active) return;
            const normalizeForUi = (item) => ({
                id: item.id,
                title: item.title || "Untitled",
                excerpt: item.excerpt || "",
                tags: Array.isArray(item.tags) ? item.tags : [],
                startDate: item.startDate || null,
                slug: item.slug || null,

                owner_id: item.owner_id || item.raw?.owner_id || null,
                createdByUid: item.createdByUid || item.raw?.createdByUid || null,

                raw: item.raw || item,
                __source: item.__source || null,
            });


            // Build AI Trip List (owned + public)
            const mergedAi = [];
            const seenAi = new Set();
            [...aiOwnedArr, ...aiPublicArr].forEach((item) => {
                const key = String(item.id || item.title).toLowerCase();
                if (!seenAi.has(key)) {
                    mergedAi.push(normalizeForUi(item));
                    seenAi.add(key);
                }
            });
            setAiTripList(mergedAi);

            // Build User Trip List (public user trips)
            const mergedUser = [];
            const seenUser = new Set();
            publicArr.forEach((item) => {
                const key = String(item.id || item.title).toLowerCase();
                if (!seenUser.has(key)) {
                    mergedUser.push(normalizeForUi(item));
                    seenUser.add(key);
                }
            });
            setUserTripList(mergedUser);

            setFetchedTrips([...mergedAi, ...mergedUser]);
            setLoadingTrips(false);
        };

        (async () => {
            setLoadingTrips(true);

            // 1) public regular trips (collection: "trips")
            try {
                const publicQuery = query(
                    collection(db, "trips"),
                    where("visibility", "==", "public"),
                    orderBy("startDate", "desc")
                );

                unsubPublicTrips = onSnapshot(
                    publicQuery,
                    (snap) => {
                        if (!active) return;
                        publicArr = snap.docs.map((d) => normalizeDoc(d, "trips"));
                        recomputeAndSet();
                    },
                    (err) => {
                        console.error("Explore: public trips subscription error", err);
                        setLoadingTrips(false);
                    }
                );
            } catch (err) {
                console.error("Explore: failed to init public trips query", err);
                setLoadingTrips(false);
            }

            // 2) public AI trips from collection "ai_trips" (visibility == public)
            try {
                aiPublicArr = [];

                // refs to read latest booleans from scroll handler (avoid stale closure)
                const aiHasMoreRef = { current: aiPublicHasMore };
                const loadingMoreRef = { current: loadingMoreAiPublic };

                const setAiHasMoreRef = (val) => { aiHasMoreRef.current = val; };
                const setLoadingMoreRef = (val) => { loadingMoreRef.current = val; };

                // initial load
                const loadInitialAiPublic = async () => {
                    try {
                        const aiCol = collection(db, "ai_trips");
                        const firstQ = query(
                            aiCol,
                            where("visibility", "==", "public"),
                            orderBy("createdAt", "desc"),
                            limit(aiPageSize)
                        );

                        const snap = await getDocs(firstQ);
                        if (!active) return;

                        aiPublicArr = snap.docs.map((d) => normalizeDoc(d, "ai_trips"));

                        const last = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
                        setAiPublicLastDoc(last);
                        aiPublicLastDocRef.current = last;
                        setAiPublicHasMore(snap.docs.length === aiPageSize);
                        setAiHasMoreRef(snap.docs.length === aiPageSize);

                        // update suggestions (best-effort)
                        try {
                            const titles = aiPublicArr
                                .map((t) => (t && t.title ? t.title : (t.raw && (t.raw.title || t.raw.name)) || "AI trip"))
                                .filter(Boolean)
                                .slice(0, 6);
                            setAiSuggestions((prev) => {
                                const seen = new Set(prev.map(x => String(x).toLowerCase()));
                                const merged = [...titles, ...prev].filter(x => {
                                    const k = String(x).toLowerCase();
                                    if (seen.has(k)) return false;
                                    seen.add(k);
                                    return true;
                                }).slice(0, 6);
                                return merged;
                            });
                        } catch (err) {
                            console.warn("Explore: failed to set aiSuggestions from public", err);
                        }

                        recomputeAndSet();
                    } catch (err) {
                        console.warn("Explore: failed to load initial ai public", err);
                    }
                };

                // loader for subsequent pages
                const loadMoreAiPublic = async () => {
                    if (!active || loadingMoreRef.current || !aiHasMoreRef.current) return;
                    const lastDoc = aiPublicLastDocRef.current;
                    if (!lastDoc) return;

                    setLoadingMoreAiPublic(true);
                    setLoadingMoreRef(true);
                    try {
                        const aiCol = collection(db, "ai_trips");
                        const nextQ = query(
                            aiCol,
                            where("visibility", "==", "public"),
                            orderBy("createdAt", "desc"),
                            startAfter(lastDoc),
                            limit(aiPageSize)
                        );

                        const snap = await getDocs(nextQ);
                        if (!active) return;

                        const more = snap.docs.map((d) => normalizeDoc(d, "ai_trips"));
                        aiPublicArr = [...aiPublicArr, ...more];

                        const last = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : aiPublicLastDoc;
                        setAiPublicLastDoc(last);
                        aiPublicLastDocRef.current = last;
                        const hasMoreNow = snap.docs.length === aiPageSize;
                        setAiPublicHasMore(hasMoreNow);
                        setAiHasMoreRef(hasMoreNow);

                        // update aiSuggestions conservatively
                        try {
                            const titles = more
                                .map((t) => (t && t.title ? t.title : (t.raw && (t.raw.title || t.raw.name)) || "AI trip"))
                                .filter(Boolean)
                                .slice(0, 6);
                            setAiSuggestions((prev) => {
                                const seen = new Set(prev.map(x => String(x).toLowerCase()));
                                const merged = [];
                                for (const s of titles.concat(prev)) {
                                    const k = String(s).toLowerCase();
                                    if (!seen.has(k)) { merged.push(s); seen.add(k); }
                                }
                                return merged.slice(0, 6);
                            });
                        } catch (err) {
                            console.warn("Explore: failed to merge aiSuggestions when loading more", err);
                        }

                        recomputeAndSet();
                    } catch (err) {
                        console.warn("Explore: failed to load more ai public", err);
                        setAiPublicHasMore(false);
                        setAiHasMoreRef(false);
                    } finally {
                        setLoadingMoreAiPublic(false);
                        setLoadingMoreRef(false);
                    }
                };

                // assign loader to hoisted ref so onScroll can call it
                loadMoreAiPublicRef.current = loadMoreAiPublic;

                // run initial load
                await loadInitialAiPublic();

                // attach scroll listener (and remove in outer effect cleanup)
                setTimeout(() => {
                    const node = resultsPaneRef.current;
                    if (node) node.addEventListener("scroll", onScroll, { passive: true });
                }, 80);

            } catch (err) {
                console.warn("Explore: failed to init ai-public paginated loader", err);
            }

            // 3) AI trips created by the current user (any visibility) in "ai_trips"
            try {
                const authUser = await waitForAuthUser();
                const uid = authUser?.uid || null;

                if (uid) {
                    const aiOwnedQuery = query(
                        collection(db, "ai_trips"),
                        where("createdByUid", "==", uid),
                        orderBy("createdAt", "desc"),
                        limit(aiPageSize)
                    );

                    unsubAiOwned = onSnapshot(
                        aiOwnedQuery,
                        (snap) => {
                            if (!active) return;
                            aiOwnedArr = snap.docs.map((d) => normalizeDoc(d, "ai_trips"));

                            try {
                                const ownedTitles = aiOwnedArr
                                    .map((t) => (t && t.title ? t.title : (t.raw && (t.raw.title || t.raw.name)) || "My AI trip"))
                                    .filter(Boolean)
                                    .slice(0, 6);

                                setAiSuggestions((prev) => {
                                    const seen = new Set();
                                    const combined = [];
                                    for (const s of ownedTitles) {
                                        const k = String(s).toLowerCase();
                                        if (!seen.has(k)) {
                                            combined.push(s);
                                            seen.add(k);
                                        }
                                    }
                                    for (const s of prev) {
                                        const k = String(s).toLowerCase();
                                        if (!seen.has(k)) {
                                            combined.push(s);
                                            seen.add(k);
                                        }
                                    }
                                    return combined.slice(0, 6);
                                });
                            } catch (err) {
                                console.warn("Explore: failed to set aiSuggestions from owned", err);
                            }
                            recomputeAndSet();
                        },
                        (err) => {
                            console.warn("Explore: ai-owned subscription error", err);
                        }
                    );
                } else {
                    aiOwnedArr = [];
                    recomputeAndSet();
                }
            } catch (err) {
                console.warn("Explore: failed to subscribe to ai-owned trips", err);
            }
        })();

        return () => {
            active = false;
            try { unsubPublicTrips && unsubPublicTrips(); } catch (e) { /* ignore */ }
            try { unsubAiOwned && unsubAiOwned(); } catch (e) { /* ignore */ }

            // remove scroll listener
            try {
                const node = resultsPaneRef.current;
                if (node) node.removeEventListener && node.removeEventListener("scroll", onScroll);
            } catch (e) { /* ignore */ }
        };
    }, [open]); // keep same deps

    useEffect(() => {
        if (!open) return;

        let active = true;

        const loadInitialGuides = async () => {
            try {
                const q = query(
                    collection(db, "guides"),
                    orderBy("updatedAt", "desc"),
                    limit(guidePageSize)
                );

                const snap = await getDocs(q);
                if (!active) return;

                const guides = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setGuideList(guides);

                const last = snap.docs[snap.docs.length - 1];
                setGuideLastDoc(last);
                setGuideHasMore(snap.docs.length === guidePageSize);

            } catch (err) {
                console.error("Failed to load guides", err);
            }
        };

        loadInitialGuides();

        return () => {
            active = false;
        };

    }, [open]);

    const loadMoreGuides = async () => {
        if (!guideHasMore || loadingMoreGuides || !guideLastDoc) return;

        setLoadingMoreGuides(true);

        try {
            const q = query(
                collection(db, "guides"),
                orderBy("updatedAt", "desc"),
                startAfter(guideLastDoc),
                limit(guidePageSize)
            );

            const snap = await getDocs(q);

            const newGuides = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setGuideList(prev => [...prev, ...newGuides]);

            const last = snap.docs[snap.docs.length - 1];
            setGuideLastDoc(last);
            setGuideHasMore(snap.docs.length === guidePageSize);

        } catch (err) {
            console.error("Failed to load more guides", err);
        } finally {
            setLoadingMoreGuides(false);
        }
    };


    // --- normalize incoming `trips` into a plain array (safe, defensive) ---
    // Prefer fetchedTrips (if available) -> then parent `trips` -> then fallback to []
    const sourceTrips = fetchedTrips !== null ? fetchedTrips : trips;

    const allTrips = useMemo(() => {
        const unwrap = (d) => {
            if (!d) return null;
            if (typeof d.data === "function") return { id: d.id, ...d.data() };
            if (d.data && typeof d.data === "object") return { id: d.id || d.data.id || d.id, ...(d.data || {}) };
            return d;
        };

        try {
            if (sourceTrips === undefined || sourceTrips === null) {
                // console.debug("Explore: sourceTrips is null/undefined");
                return [];
            }
            if (Array.isArray(sourceTrips)) {
                const mapped = sourceTrips.map(unwrap).filter(Boolean);
                // console.debug("Explore: sourceTrips is Array -> length", mapped.length, { sample: mapped[0] });
                return mapped;
            }
            if (Array.isArray(sourceTrips.trips)) {
                const mapped = sourceTrips.trips.map(unwrap).filter(Boolean);
                // console.debug("Explore: sourceTrips has .trips array -> length", mapped.length);
                return mapped;
            }
            if (sourceTrips.docs && Array.isArray(sourceTrips.docs)) {
                const mapped = sourceTrips.docs.map(unwrap).filter(Boolean);
                // console.debug("Explore: sourceTrips is QuerySnapshot-like -> docs", sourceTrips.docs.length, "mapped", mapped.length);
                return mapped;
            }
            const likelyTripKeys = ["title", "startDate", "tags", "slug", "id", "trip_id"];
            if (typeof sourceTrips === "object" && likelyTripKeys.some(k => k in sourceTrips)) {
                const single = unwrap(sourceTrips);
                // console.debug("Explore: sourceTrips looks like a single trip -> using single item", { sample: single && { id: single.id, title: single.title } });
                return single ? [single] : [];
            }
            if (typeof sourceTrips === "object") {
                const vals = Object.values(sourceTrips).map(unwrap).filter(Boolean);
                // console.debug("Explore: sourceTrips is object map -> keys", Object.keys(sourceTrips).slice(0, 8), "valuesLength", vals.length, "sample:", vals[0]);
                return vals;
            }
            // console.debug("Explore: unknown sourceTrips shape:", sourceTrips);
            return [];
        } catch (err) {
            console.error("Explore: error normalizing sourceTrips", err);
            return [];
        }
    }, [sourceTrips]);


    // tags fallback
    const allTags = (Array.isArray(tags) && tags.length > 0) ? tags : sampleTags;

    // derive popular tags: if tags are objects use name, otherwise string
    const popularTags = useMemo(() => {
        return allTags.slice(0, 12).map((t) => (typeof t === "string" ? t : t.name || t));
    }, [allTags]);

    // filter logic for suggestions/results
    const normalizedQuery = (searchQuery || "").trim().toLowerCase();
    const activeQueryTag = activeTag || null;
    useEffect(() => {
        const delay = setTimeout(() => {
            if (normalizedQuery.length >= 3) {
                searchGlobal(normalizedQuery);
            } else {
                setAiSearchResults(null);
            }
        }, 400);

        return () => clearTimeout(delay);
    }, [normalizedQuery]);
    const filteredTrips = useMemo(() => {
        const buildHay = (t) =>
            (
                (t.title || "") +
                " " +
                (t.excerpt || "") +
                " " +
                (Array.isArray(t.tags) ? t.tags.join(" ") : (t.tags || ""))
            ).toLowerCase();

        // If user typed a query -> normal text filter
        if (normalizedQuery) {
            return allTrips.filter((t) => buildHay(t).includes(normalizedQuery));
        }

        // If user selected a tag -> filter by tag
        if (activeQueryTag) {
            const tagLower = String(activeQueryTag).toLowerCase();
            return allTrips.filter((t) =>
                Array.isArray(t.tags) && t.tags.some((tg) => String(tg).toLowerCase() === tagLower)
            );
        }

        // No query & no tag -> suggested trips (include most recent withDate + withoutDate)
        const withDate = allTrips
            .filter((t) => t.startDate)
            .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        const withoutDate = allTrips.filter((t) => !t.startDate);

        // suggested base set
        const suggested = [...withDate.slice(0, 6), ...withoutDate.slice(0, 6)];

        // Merge AI results at the front when available (aiResults is a UI-friendly array)
        // avoid duplicates by id or title
        if (Array.isArray(aiResults) && aiResults.length > 0) {
            const seenKeys = new Set();
            const addKey = (i) => {
                const key = String(i.id || i.title || "").toLowerCase();
                seenKeys.add(key);
            };

            // mark keys that are already in suggested
            suggested.forEach((s) => addKey(s));

            // build final list with aiResults first (unique), then suggested (unique)
            const merged = [];
            for (const ai of aiResults) {
                const key = String(ai.id || ai.title || "").toLowerCase();
                if (!seenKeys.has(key)) {
                    merged.push(ai);
                    addKey(ai);
                }
            }
            merged.push(...suggested.filter((s) => {
                const key = String(s.id || s.title || "").toLowerCase();
                return seenKeys.has(key); // now seenKeys includes those we added; this preserves the original suggested order
            }));

            // final result: ensure it's an array of unified trip-like objects
            return merged;
        }

        return suggested;
    }, [allTrips, normalizedQuery, activeQueryTag, aiResults]);

    const matchesSearch = (item, query) => {
        if (!query) return true;

        const q = query.toLowerCase().trim();

        const safe = (v) => (v ? String(v).toLowerCase() : "");

        // Collect searchable fields
        const fields = [
            safe(item.title),
            safe(item.excerpt),
            safe(item.summary),
            safe(item.destination),
        ];

        // Tags
        if (Array.isArray(item.tags)) {
            fields.push(...item.tags.map(safe));
        }

        // AI Trip Deep Search
        if (item.itinerary && Array.isArray(item.itinerary)) {
            item.itinerary.forEach(day => {
                fields.push(safe(day.title));
                fields.push(safe(day.summary));

                if (Array.isArray(day.activities)) {
                    day.activities.forEach(a => {
                        fields.push(safe(a.title));
                        fields.push(safe(a.location));
                    });
                }
            });
        }

        if (item.highlights && Array.isArray(item.highlights)) {
            fields.push(...item.highlights.map(safe));
        }

        if (item.overview?.destination_info?.description) {
            fields.push(safe(item.overview.destination_info.description));
        }

        // Guides specific
        if (item.city?.name) fields.push(safe(item.city.name));
        if (item.country?.name) fields.push(safe(item.country.name));

        // Join everything
        const haystack = fields.join(" ");

        return haystack.includes(q);
    };

    // --- Filter helper for lists (reuse same hay-building logic)
    const filterListByQuery = (list) => {
        if (!Array.isArray(list)) return [];
        const q = normalizedQuery;
        const tag = activeQueryTag ? String(activeQueryTag).toLowerCase() : null;

        const buildHay = (t) =>
            (
                (t.title || "") +
                " " +
                (t.excerpt || "") +
                " " +
                (Array.isArray(t.tags) ? t.tags.join(" ") : (t.tags || ""))
            ).toLowerCase();

        // If text query, use it
        if (q) {
            return list.filter((t) => buildHay(t).includes(q));
        }

        // If tag selected, use it
        if (tag) {
            return list.filter((t) =>
                Array.isArray(t.tags) && t.tags.some((tg) => String(tg).toLowerCase() === tag)
            );
        }

        // No query / tag -> return original order (no change)
        return list;
    };

    // filtered lists used by UI components (memoized for perf)
    const filteredUserTripList = useMemo(() => filterListByQuery(userTripList), [userTripList, normalizedQuery, activeQueryTag]);
    const filteredAiTripList = useMemo(() => filterListByQuery(aiTripList), [aiTripList, normalizedQuery, activeQueryTag]);
    const aiDisplayList = aiSearchResults ?? filteredAiTripList;

    const onSubmit = async (e) => {
        if (aiLoading) return;
        closedDuringGenerationRef.current = false;

        e && e.preventDefault();
        const q = (searchQuery || "").trim();

        if (q.length < 5) {
            setAiError("Please enter at least 5 characters.");
            return;
        }

        setAiLoading(true);
        const authUser = await waitForAuthUser();

        if (!authUser) {
            setAiGenerationError("Please log in to generate AI trips.");
            setAiGenerationStatus("error");
            setAiLoading(false);
            return;
        }

        try {
            // 1) Try structured schema-first approach
            let parsed = null;
            try {
                parsed = await generateStructuredTrip(q, { max_tokens: 1400 });
            } catch (structuredErr) {
                console.error("❌ Structured AI generation failed:", structuredErr);

                let message = "Unable to generate AI trip currently. Please try again.";

                if (structuredErr?.code === "DAILY_LIMIT") {
                    message = "🚫 You've reached your daily AI limit. Upgrade to Pro to extend your limit";
                }
                else if (structuredErr?.code === "COOLDOWN") {
                    message = "⏳ Please wait 30 seconds before generating another trip.";
                }
                else if (structuredErr?.code === "UNAUTHORIZED") {
                    message = "🔐 Please log in to generate AI trips.";
                }
                else if (structuredErr?.message) {
                    message = structuredErr.message;
                }

                setAiGenerationError(message);
                setAiGenerationStatus("error");
                setAiLoading(false);
                return;
            }
            // 2) If structured parsed object exists, normalize it to your internal shape
            let normalized;
            if (parsed) {
                // parsed should already match schema — but run through your normalizer for consistency
                normalized = normalizeAiPayload(parsed);
                // 🚨 Validate structured data before saving
                if (
                    !normalized.itinerary ||
                    !Array.isArray(normalized.itinerary) ||
                    normalized.itinerary.length === 0
                ) {
                    console.error("❌ AI returned invalid or empty itinerary:", normalized);

                    setAiGenerationStatus(null);
                    setPendingAiTrip(null);
                    setAiGenerationError("Unable to generate AI trip currently. Please try again.");
                    setAiGenerationStatus("error");

                    setAiLoading(false);
                    return;
                }


                // ensure raw contains the original parsed object for debugging
                normalized.raw = parsed;
            }
            // 4) Final safe defaults (defensive): ensure itinerary & trip_days are sane
            if (!normalized.trip_days && Array.isArray(normalized.itinerary)) {
                normalized.trip_days = normalized.itinerary.length || 0;
            }
            if (!normalized.tags || !Array.isArray(normalized.tags)) {
                normalized.tags = ["ai", "suggested"];
            }

            // 5) Save to Firestore
            const saveRes = await saveAiTripToFirestore(normalized);
            if (saveRes) {
                normalized.id = saveRes.id;
                normalized.slug = saveRes.slug;   // ← ADD THIS
                normalized._savedPath = saveRes.path;
                normalized._createdByUid = saveRes.createdByUid;
            }
            else {
                normalized._saved = false;
            }
            // IMPORTANT: store a lightweight normalized AI result for UI use
            // ensure it has title, excerpt, tags and id if available
            const aiForUi = {
                id: normalized.id || normalized._tempId || `${normalized.title || "ai-trip"}-${Date.now()}`,
                title: normalized.title || normalized.name || "AI trip",
                excerpt: normalized.excerpt || (normalized.description && normalized.description.slice(0, 240)) || "",
                tags: Array.isArray(normalized.tags) ? normalized.tags : (normalized.tags ? [normalized.tags] : ["ai", "suggested"]),
                raw: normalized.raw || normalized,
                // keep the full object if you want later
                __full: normalized,
            };
            setAiResults((prev) => {
                // put newest AI result at the front, keep unique by id/title
                const merged = [aiForUi, ...(prev || [])];
                const seen = new Set();
                return merged.filter((i) => {
                    const key = String(i.id || i.title || "").toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                }).slice(0, 6); // keep a few at most
            });
            // 6) push to AI context and navigate
            setAiTrip(normalized);

            // If modal still open → navigate normally
            if (!closedDuringGenerationRef.current) {
                if (normalized.slug) {
                    navigate(`/ai/${normalized.slug}`);
                } else {
                    navigate(`/ai/${normalized.id}`);
                }
                setOpen(false);
            } else {
                // Modal was closed → show toast instead
                setPendingAiTrip(normalized);
                setAiGenerationStatus("ready");

            }

            setAiLoading(false);


        } catch (err) {
            console.error("Error during AI search+save:", err);

            setAiGenerationStatus(null);  // 🔥 important
            setPendingAiTrip(null);

            setAiGenerationError("Unable to generate AI trip currently. Please try again.");
            setAiGenerationStatus("error");

            setAiLoading(false);
        }

    };

    const overlay = open ? (
        <div className="search-overlay" role="dialog" aria-modal="true" aria-label="Search trips">
            <div className={`overlay-backdrop ${open ? "open" : ""}`} onClick={() => {
                if (aiLoading) {
                    closedDuringGenerationRef.current = true;
                    setAiGenerationStatus("loading");
                }

                setOpen(false);
            }}
            />
            <div className={`search-card ${open ? "open" : ""}`}>
                {/* ... form and results ... */}
            </div>
        </div>
    ) : null;

    const filteredGuides = useMemo(() => {
        if (!Array.isArray(guideList)) return [];

        const q = normalizedQuery;
        const tag = activeQueryTag?.toLowerCase();

        return guideList.filter((g) => {
            const hay =
                (
                    g.title +
                    " " +
                    (g.city?.name || "") +
                    " " +
                    (g.country?.name || "") +
                    " " +
                    (g.guide?.subtitle || "")
                ).toLowerCase();

            if (q && !hay.includes(q)) return false;

            if (tag && g.city?.name?.toLowerCase() !== tag) return false;

            return true;
        });
    }, [guideList, normalizedQuery, activeQueryTag]);

    const searchGlobal = async (term) => {
        if (!term || term.length < 3) {
            setAiSearchResults(null);
            setSearchLoading(false);
            return;
        }

        const keyword = term.toLowerCase().trim();
        const words = keyword.split(/\s+/);

        try {
            setSearchLoading(true);   // 🔥 START LOADING

            const results = [];

            // 🔹 AI TRIPS
            const aiSnap = await getDocs(query(
                collection(db, "ai_trips"),
                where("visibility", "==", "public"),
                where("tags", "array-contains-any", words),
                limit(10)
            ));

            aiSnap.forEach(doc => {
                results.push({ id: doc.id, type: "ai_trip", ...doc.data() });
            });

            // 🔹 COMMUNITY TRIPS
            const tripsSnap = await getDocs(query(
                collection(db, "trips"),
                where("visibility", "==", "public"),
                where("tags", "array-contains-any", words),
                limit(10)
            ));

            tripsSnap.forEach(doc => {
                results.push({ id: doc.id, type: "trip", ...doc.data() });
            });

            // 🔹 GUIDES
            const guidesSnap = await getDocs(query(
                collection(db, "guides"),
                where("tags", "array-contains-any", words),
                limit(10)
            ));

            guidesSnap.forEach(doc => {
                results.push({ id: doc.id, type: "guide", ...doc.data() });
            });

            // 🔹 PLACES
            const placesSnap = await getDocs(query(
                collection(db, "places"),
                where("tags", "array-contains-any", words),
                limit(10)
            ));

            placesSnap.forEach(doc => {
                results.push({ id: doc.id, type: "place", ...doc.data() });
            });

            setAiSearchResults(results);

        } catch (err) {
            console.error("Global search failed:", err);
        } finally {
            setSearchLoading(false);   // 🔥 STOP LOADING
        }
    };


    // =============================
    // 🔎 SEARCH MODE HELPERS
    // =============================
    const isSearching = searchQuery.trim().length >= 3 && Array.isArray(aiSearchResults);

    const searchAiTripsList = (aiSearchResults || []).filter(r => r.type === "ai_trip");
    const searchTripList = (aiSearchResults || []).filter(r => r.type === "trip");
    const searchGuideList = (aiSearchResults || []).filter(r => r.type === "guide");
    const searchPlaceList = (aiSearchResults || []).filter(r => r.type === "place");

    return (
        <div className="hp-explore">
            <div className="explore-wrap" aria-hidden={!open}>
                <div className="explore-spot">
                    {open && (
                        <div className="search-overlay" role="dialog" aria-modal="true" aria-label="Search trips">
                            <div className={`overlay-backdrop ${open ? "open" : ""}`} onClick={() => {
                                if (aiLoading) {
                                    closedDuringGenerationRef.current = true;
                                    setAiGenerationStatus("loading");
                                }

                                setOpen(false);
                            }}
                            />

                            <div className={`search-card ${open ? "open" : ""}`}>
                                <form onSubmit={(e) => e.preventDefault()} style={{ display: "flex", flexDirection: "column" }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: 16,
                                            width: "100%",
                                            alignItems: "flex-start"
                                        }}
                                    >

                                        {/* Search Icon */}
                                        <div style={{ paddingTop: 12 }}>
                                            <svg
                                                width="22"
                                                height="22"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                aria-hidden="true"
                                                style={{ color: "var(--text-color)" }}
                                            >
                                                <path
                                                    d="M21 21l-4.35-4.35"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <circle
                                                    cx="11"
                                                    cy="11"
                                                    r="6"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                />
                                            </svg>
                                        </div>

                                        {/* Input + Subtext */}
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>

                                            {/* Input Row */}
                                            <div
                                                style={{
                                                    position: "relative",
                                                    display: "flex",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <input
                                                    ref={inputRef}
                                                    className="search-input"
                                                    placeholder={`${aiPlaceholderExamples[placeholderIndex]}`}
                                                    value={searchQuery}
                                                    onChange={(e) => {
                                                        setSearchQuery(e.target.value);
                                                        setActiveTag(null);
                                                        setAiError(null);
                                                    }}
                                                    aria-label="Search trips"
                                                    style={{
                                                        width: "100%",
                                                        height: 48,
                                                        paddingRight: 36
                                                    }}
                                                />

                                                {searchLoading && (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            right: 10,
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                        }}
                                                    >
                                                        <div className="search-loader" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Subtext */}
                                            <div
                                                style={{
                                                    marginTop: 6,
                                                    fontSize: 13,
                                                    color: "#6b7280"
                                                }}
                                            >
                                                ✨ Describe your dream trip and press Create — AI builds the itinerary.
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: 10,
                                                alignItems: "center",
                                                height: 48   // matches input height
                                            }}
                                        >
                                            <button
                                                type="button"
                                                className="btn-ghost"
                                                onClick={() => {
                                                    setSearchQuery("");
                                                    setActiveTag(null);
                                                    inputRef.current && inputRef.current.focus();
                                                }}
                                                style={{ height: 40 }}
                                            >
                                                Clear
                                            </button>

                                            <button
                                                type="button"
                                                className="ai-generate-btn"
                                                onClick={onSubmit}
                                                disabled={aiLoading || searchQuery.trim().length < 5}
                                                style={{ height: 40 }}
                                            >
                                                {aiLoading ? (
                                                    <>
                                                        <span className="ai-btn-loader"></span>
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>✨ Create AI Trip</>
                                                )}
                                            </button>
                                        </div>

                                    </div>

                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                                        <div className="hint">Search public trips across the community. Press Esc to close.
                                            <div className="suggestion-row" style={{ marginTop: 10 }}>
                                                {aiSuggestions.map((s, i) => (
                                                    <div
                                                        key={`ai-sug-${i}`}
                                                        className="tag-pill"
                                                        onClick={() => setSearchQuery(s)}
                                                        style={{ cursor: "pointer" }}
                                                        title="Use this suggested AI trip title as a search / prompt"
                                                    >
                                                        {s}
                                                    </div>
                                                ))}
                                            </div>

                                        </div>
                                        <div style={{ fontSize: 13, color: "#6b7280" }}>{allTrips.length} trips</div>
                                    </div>

                                    {/* tag pills (inside overlay) */}
                                    <div className="suggestion-row" role="list" aria-label="popular tags">
                                        {popularTags.slice(0, 10).map((t) => (
                                            <div
                                                key={t}
                                                role="button"
                                                tabIndex={0}
                                                className={`tag-pill ${activeTag === t ? "active" : ""}`}
                                                onClick={() => {
                                                    setActiveTag(t);
                                                    setSearchQuery("");
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        setActiveTag(t);
                                                        setSearchQuery("");
                                                    }
                                                }}
                                            >
                                                #{t}
                                            </div>
                                        ))}
                                    </div>
                                </form>
                                <div
                                    ref={resultsPaneRef}
                                    className="results-pane"
                                    aria-live="polite"
                                    style={{ marginTop: 20, overflowY: "auto", maxHeight: "55vh" }}
                                >

                                    {aiLoading && (
                                        <div style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "60px 20px",
                                            textAlign: "center",
                                        }}>
                                            <div className="ai-loader"></div>
                                            <div style={{ marginTop: 14, fontSize: 14, color: "#6b7280" }}>
                                                Generating your AI trip plan...
                                            </div>
                                        </div>
                                    )}

                                    {!aiLoading && (
                                        <>
                                            {isSearching ? (
                                                <>
                                                    {/* ================= SEARCH MODE ================= */}

                                                    {/* ===== GUIDES ===== */}
                                                    {searchGuideList.length > 0 && (
                                                        <section style={{ marginTop: 28 }}>
                                                            <div style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                marginBottom: 8
                                                            }}>
                                                                <h3 style={{ margin: 0 }}>Travel Guides</h3>
                                                                <div style={{ fontSize: 13, color: "#6b7280" }}>
                                                                    {searchGuideList.length} Guides
                                                                </div>
                                                            </div>

                                                            <div className="results">
                                                                {searchGuideList.map(g => (
                                                                    <div
                                                                        key={g.id}
                                                                        className="result-card"
                                                                        onClick={() => {
                                                                            navigate(`/${g.slug}`);
                                                                            setOpen(false);
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={g.guide?.heroImage}
                                                                            alt={g.title}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: 160,
                                                                                objectFit: "cover",
                                                                                borderRadius: 8,
                                                                                marginBottom: 8,
                                                                            }}
                                                                        />
                                                                        <strong>{g.title}</strong>
                                                                        <div className="result-meta">
                                                                            {g.guide?.subtitle}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </section>
                                                    )}

                                                    {/* ===== COMMUNITY TRIPS ===== */}
                                                    {searchTripList.length > 0 && (
                                                        <section style={{ marginTop: 28 }}>
                                                            <div style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                marginBottom: 8
                                                            }}>
                                                                <h3 style={{ margin: 0 }}>Community Trips</h3>
                                                                <div style={{ fontSize: 13, color: "#6b7280" }}>
                                                                    {searchTripList.length} Trips
                                                                </div>
                                                            </div>

                                                            <TripsCarousel
                                                                items={searchTripList}
                                                                defaultPerSlide={3}
                                                                onSelect={(item) => navigateToTrip(item)}
                                                            />
                                                        </section>
                                                    )}

                                                    {/* ===== AI TRIPS ===== */}
                                                    {searchAiTripsList.length > 0 && (
                                                        <section style={{ marginTop: 28 }}>
                                                            <div style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                marginBottom: 8
                                                            }}>
                                                                <h3 style={{ margin: 0 }}>AI Trip Ideas</h3>
                                                                <div style={{ fontSize: 13, color: "#6b7280" }}>
                                                                    {searchAiTripsList.length} AI Trips
                                                                </div>
                                                            </div>

                                                            <div className="results">
                                                                {searchAiTripsList.map(r => (
                                                                    <div
                                                                        key={r.id}
                                                                        className="result-card ai-source"
                                                                        onClick={() => {
                                                                            if (r.slug) {
                                                                                setAiTrip(r);
                                                                                navigate(`/ai/${r.slug}`);
                                                                                setOpen(false);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <strong>{r.title}</strong>
                                                                        <div className="result-meta">
                                                                            {r.excerpt || "AI-generated itinerary"}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </section>
                                                    )}

                                                    {/* ===== PLACES ===== */}
                                                    {searchPlaceList.length > 0 && (
                                                        <section style={{ marginTop: 28 }}>
                                                            <div style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                marginBottom: 8
                                                            }}>
                                                                <h3 style={{ margin: 0 }}>Places</h3>
                                                                <div style={{ fontSize: 13, color: "#6b7280" }}>
                                                                    {searchPlaceList.length} Places
                                                                </div>
                                                            </div>

                                                            <div className="results">
                                                                {searchPlaceList.map(p => (
                                                                    <div
                                                                        key={p.id}
                                                                        className="result-card"
                                                                        onClick={() => {
                                                                            navigate(`/places/${p.slug}`);
                                                                            setOpen(false);
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={p.images?.[0]}
                                                                            alt={p.name}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: 160,
                                                                                objectFit: "cover",
                                                                                borderRadius: 8,
                                                                                marginBottom: 8,
                                                                            }}
                                                                        />
                                                                        <strong>{p.name}</strong>
                                                                        <div className="result-meta">
                                                                            {p.shortDescription}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </section>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {/* ================= DEFAULT MODE ================= */}

                                                    {/* ===== GUIDES ===== */}
                                                    {filteredGuides.length > 0 && (
                                                        <section style={{ marginTop: 28 }}>
                                                            <div style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                marginBottom: 8
                                                            }}>
                                                                <h3 style={{ margin: 0 }}>Travel Guides</h3>
                                                                <div style={{ fontSize: 13, color: "#6b7280" }}>
                                                                    {filteredGuides.length} Guides
                                                                </div>
                                                            </div>

                                                            <div className="results">
                                                                {filteredGuides.map(g => (
                                                                    <div
                                                                        key={g.id}
                                                                        className="result-card"
                                                                        onClick={() => {
                                                                            navigate(`/${g.slug}`);
                                                                            setOpen(false);
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={g.guide?.heroImage}
                                                                            alt={g.title}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: 160,
                                                                                objectFit: "cover",
                                                                                borderRadius: 8,
                                                                                marginBottom: 8,
                                                                            }}
                                                                        />
                                                                        <strong>{g.title}</strong>
                                                                        <div className="result-meta">
                                                                            {g.guide?.subtitle}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* 🔁 Load More Guides */}
                                                            {guideHasMore && (
                                                                <div style={{ textAlign: "center", marginTop: 14 }}>
                                                                    <button
                                                                        className="btn-ghost"
                                                                        onClick={loadMoreGuides}
                                                                        disabled={loadingMoreGuides}
                                                                    >
                                                                        {loadingMoreGuides ? "Loading..." : "Load More"}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </section>
                                                    )}

                                                    {/* ===== COMMUNITY ===== */}
                                                    {filteredUserTripList.length > 0 && (
                                                        <section style={{ marginTop: 28 }}>
                                                            <h3>Community Trips</h3>
                                                            <TripsCarousel
                                                                items={filteredUserTripList}
                                                                defaultPerSlide={3}
                                                                onSelect={(item) => navigateToTrip(item)}
                                                            />
                                                        </section>
                                                    )}

                                                    {/* ===== AI TRIPS ===== */}
                                                    {filteredAiTripList.length > 0 && (
                                                        <section style={{ marginTop: 28 }}>
                                                            <div style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                marginBottom: 8
                                                            }}>
                                                                <h3 style={{ margin: 0 }}>AI Trip Ideas</h3>
                                                                <div style={{ fontSize: 13, color: "#6b7280" }}>
                                                                    {filteredAiTripList.length} AI Trips
                                                                </div>
                                                            </div>

                                                            <div className="results">
                                                                {filteredAiTripList.map(r => (
                                                                    <div
                                                                        key={r.id}
                                                                        className="result-card ai-source"
                                                                        onClick={() => {
                                                                            if (r.slug) {
                                                                                setAiTrip(r);
                                                                                navigate(`/ai/${r.slug}`);
                                                                                setOpen(false);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <strong>{r.title}</strong>
                                                                        <div className="result-meta">
                                                                            {r.excerpt || "AI-generated itinerary"}
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                {/* 🔁 Load More AI */}
                                                                {aiPublicHasMore && (
                                                                    <div style={{ textAlign: "center", marginTop: 14 }}>
                                                                        <button
                                                                            className="btn-ghost"
                                                                            onClick={() => loadMoreAiPublicRef.current?.()}
                                                                            disabled={loadingMoreAiPublic}
                                                                        >
                                                                            {loadingMoreAiPublic ? "Loading..." : "Load More"}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* 🔄 Infinite Scroll Loader */}
                                                            {loadingMoreAiPublic && (
                                                                <div style={{
                                                                    textAlign: "center",
                                                                    padding: 10,
                                                                    color: "#6b7280",
                                                                }}>
                                                                    Loading more AI trips…
                                                                </div>
                                                            )}
                                                        </section>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}

                                </div>

                            </div>
                        </div>
                    )}
                </div>

            </div>

        </div>

    );
}
